import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, formatError, REFEICAO_LABEL } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, EmptyState, Badge } from "@/components/Brutal";
import { toast } from "sonner";
import { Plus, Trash2, Printer, UtensilsCrossed, Coffee, Soup, Sandwich } from "lucide-react";

const today = () => new Date().toISOString().split("T")[0];
const empty = () => ({ visitante_nome: "", visitante_documento: "", empresa: "", motivo_visita: "", tipo_refeicao: "almoco", valor: 0, data_refeicao: today(), observacoes: "" });

const TIPO_ICON = { cafe_da_manha: Coffee, almoco: UtensilsCrossed, janta: Soup, lanche: Sandwich };
const TIPO_COLOR = { cafe_da_manha: "warn", almoco: "orange", janta: "default", lanche: "green" };

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const t = await api.get("/visitor-tickets");
    setItems(t.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, valor: parseFloat(form.valor) || 0, data_refeicao: form.data_refeicao || null };
      await api.post("/visitor-tickets", payload);
      toast.success("Ticket emitido");
      setOpen(false); setForm(empty()); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!confirm("Excluir ticket?")) return;
    try { await api.delete(`/visitor-tickets/${id}`); toast.success("Excluído"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  // Aggregate by type for stats
  const stats = items.reduce((acc, t) => {
    acc.total = (acc.total || 0) + 1;
    acc.valor = (acc.valor || 0) + (t.valor || 0);
    acc[t.tipo_refeicao] = (acc[t.tipo_refeicao] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="tickets-page">
      <SectionTitle action={<Btn onClick={() => { setForm(empty()); setOpen(true); }} data-testid="add-ticket-btn"><Plus className="w-4 h-4" />Novo Ticket</Btn>}>
        Tickets de Refeição · <UtensilsCrossed className="inline w-5 h-5 -mt-1" />
      </SectionTitle>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#555]">Total Tickets</div><div className="font-display font-black text-2xl">{stats.total || 0}</div></div>
        <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#FF4F00]">Valor Total</div><div className="font-mono-data font-bold text-base">{fmtBRL(stats.valor || 0)}</div></div>
        <div className="brutal-card-sm p-3 bg-[#FFF8E1]"><div className="font-mono-data text-[10px] uppercase text-[#5C4400]">Café</div><div className="font-display font-black text-2xl">{stats.cafe_da_manha || 0}</div></div>
        <div className="brutal-card-sm p-3 bg-[#FFE9D9]"><div className="font-mono-data text-[10px] uppercase text-[#7C2D12]">Almoço</div><div className="font-display font-black text-2xl">{stats.almoco || 0}</div></div>
        <div className="brutal-card-sm p-3 bg-[#E8F0EA]"><div className="font-mono-data text-[10px] uppercase text-[#0F2D1F]">Janta / Lanche</div><div className="font-display font-black text-2xl">{(stats.janta || 0) + (stats.lanche || 0)}</div></div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Nº</th><th>Data</th><th>Visitante</th><th>Documento</th><th>Empresa</th><th>Função / Motivo</th><th>Refeição</th><th className="text-right">Valor</th><th className="w-24">Ações</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && items.length === 0 && <EmptyState message="Nenhum ticket emitido." cols={9} />}
            {items.map((t) => {
              const Icon = TIPO_ICON[t.tipo_refeicao] || UtensilsCrossed;
              return (
                <tr key={t.id}>
                  <td className="font-mono-data font-bold">{t.numero}</td>
                  <td className="font-mono-data text-xs">{fmtDate(t.data_refeicao)}</td>
                  <td className="font-medium">{t.visitante_nome}</td>
                  <td className="font-mono-data text-xs">{t.visitante_documento || "—"}</td>
                  <td className="text-sm">{t.empresa || "—"}</td>
                  <td className="text-sm text-[#555]">{t.motivo_visita || "—"}</td>
                  <td><Badge variant={TIPO_COLOR[t.tipo_refeicao]}><Icon className="w-3 h-3 inline mr-1" />{REFEICAO_LABEL[t.tipo_refeicao]}</Badge></td>
                  <td className="text-right font-mono-data font-bold">{fmtBRL(t.valor)}</td>
                  <td><div className="flex gap-1">
                    <a href={`/imprimir/ticket/${t.id}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111] inline-flex" title="Imprimir ticket"><Printer className="w-3.5 h-3.5" /></a>
                    <button onClick={() => remove(t.id)} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Novo Ticket de Refeição" size="lg">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="ticket-form">
          <Field label="Nome do Visitante *" required value={form.visitante_nome} onChange={(v) => setForm({ ...form, visitante_nome: v })} colspan />
          <Field label="CPF / RG" value={form.visitante_documento} onChange={(v) => setForm({ ...form, visitante_documento: v })} />
          <Field label="Empresa / Origem" value={form.empresa} onChange={(v) => setForm({ ...form, empresa: v })} />
          <Field label="Função ou Motivo da visita" value={form.motivo_visita} onChange={(v) => setForm({ ...form, motivo_visita: v })} placeholder="Ex: Fiscal, Consultor, Reunião..." />
          <Field label="Data" type="date" value={form.data_refeicao} onChange={(v) => setForm({ ...form, data_refeicao: v })} />
          <SelectField label="Refeição *" required value={form.tipo_refeicao} onChange={(v) => setForm({ ...form, tipo_refeicao: v })}
            options={Object.entries(REFEICAO_LABEL).map(([k, l]) => ({ value: k, label: l }))} />
          <Field label="Valor (R$) *" type="number" required value={form.valor} onChange={(v) => setForm({ ...form, valor: v })} />
          <TextArea label="Observações" value={form.observacoes} onChange={(v) => setForm({ ...form, observacoes: v })} />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-ticket-btn">Emitir Ticket</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
